import { useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { MagnifyingGlass, Copy, DownloadSimple, ShieldCheck, Clock } from "@phosphor-icons/react";

const QUICK_ORDERS = ["GBS-202608-014", "GBS-202608-011", "GBS-202608-013"];

const rupiah = (n) => "Rp " + Math.round(Number(n) || 0).toLocaleString("id-ID");

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`rounded-xl px-4 py-2 text-sm font-bold transition-colors ${
        active ? "bg-[#6A38C7] text-white" : "bg-white text-slate-500 hover:bg-rose-50"
      }`}
    >
      {children}
    </button>
  );
}

function MultiOrderAudit() {
  const [orderNo, setOrderNo] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = useCallback(async (code) => {
    setLoading(true);
    setResult(null);
    try {
      const q = code ? `?order_no=${encodeURIComponent(code)}` : "";
      const { data } = await api.get(`/admin/audit/multi-orders${q}`);
      setResult(data);
      toast.success(`Audit selesai — ${data.count} pesanan`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const copyReport = () => {
    if (!result) return;
    navigator.clipboard.writeText(JSON.stringify(result, null, 2));
    toast.success("Laporan termasking disalin");
  };

  const downloadJson = () => {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-multi-order-${orderNo || "semua"}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="multi-order-audit">
      <div className="rounded-2xl border border-rose-100/70 bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="mb-1 block text-xs font-semibold text-slate-500">Kode Pesanan (order_no)</label>
            <input
              data-testid="audit-order-input"
              value={orderNo}
              onChange={(e) => setOrderNo(e.target.value.trim())}
              placeholder="GBS-YYYYMM-000 (kosongkan untuk semua)"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-[#6A38C7] focus:outline-none"
            />
          </div>
          <button
            data-testid="audit-run-btn"
            onClick={() => run(orderNo)}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl bg-[#6A38C7] px-4 py-2 text-sm font-bold text-white hover:bg-[#5a2eb0] disabled:opacity-50"
          >
            <MagnifyingGlass size={16} weight="bold" /> {loading ? "Menjalankan…" : "Jalankan Audit Read-Only"}
          </button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-slate-400">Cepat:</span>
          {QUICK_ORDERS.map((code) => (
            <button
              key={code}
              data-testid={`audit-quick-${code}`}
              onClick={() => { setOrderNo(code); run(code); }}
              className="rounded-lg bg-slate-100 px-2.5 py-1 font-mono text-xs font-bold text-slate-600 hover:bg-[#EEE6FF] hover:text-[#6A38C7]"
            >
              {code}
            </button>
          ))}
        </div>
      </div>

      {result && (
        <div className="mt-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-4 text-sm">
              <span className="text-slate-500">Estimasi uang nyata: <b className="text-emerald-600">{rupiah(result.totals?.real_money_estimate)}</b></span>
              <span className="text-slate-500">Jika dijumlah semua: <b className="text-rose-600">{rupiah(result.totals?.if_summing_all)}</b></span>
              <span className="text-slate-500">Potensi over-count: <b className="text-amber-600">{rupiah(result.totals?.overcount)}</b></span>
            </div>
            <div className="flex gap-2">
              <button data-testid="audit-copy-btn" onClick={copyReport} className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200">
                <Copy size={14} weight="bold" /> Salin Laporan Termasking
              </button>
              <button data-testid="audit-download-btn" onClick={downloadJson} className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200">
                <DownloadSimple size={14} weight="bold" /> Unduh JSON Termasking
              </button>
            </div>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-rose-100/70 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-100 bg-slate-50 text-xs text-slate-500">
                <tr>
                  <th className="px-3 py-2.5 font-semibold">Pesanan</th>
                  <th className="px-3 py-2.5 font-semibold">Nama</th>
                  <th className="px-3 py-2.5 font-semibold">Sumber</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Grand Total</th>
                  <th className="px-3 py-2.5 font-semibold text-center">Item</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Σ Harga Anak</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Bayar Induk</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Bayar Anak</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Estimasi Nyata</th>
                  <th className="px-3 py-2.5 font-semibold text-center">Risiko Ganda</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.length === 0 && (
                  <tr><td colSpan={10} className="px-4 py-6 text-center text-slate-400">Tidak ada data.</td></tr>
                )}
                {result.rows.map((r, i) => (
                  <tr key={i} data-testid={`audit-row-${r.order_no}`} className="border-b border-slate-50 last:border-0">
                    <td className="px-3 py-2.5 font-mono font-bold text-slate-700">{r.order_no}</td>
                    <td className="px-3 py-2.5 text-slate-600">{r.nama_masked}</td>
                    <td className="px-3 py-2.5 text-slate-500">{r.source}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{rupiah(r.grand_total)}</td>
                    <td className="px-3 py-2.5 text-center text-slate-600">{r.children_count}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{rupiah(r.child_harga_sum)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{rupiah(r.parent_paid)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{rupiah(r.child_paid)}</td>
                    <td className="px-3 py-2.5 text-right font-bold text-emerald-600">{rupiah(r.real_money_estimate)}</td>
                    <td className="px-3 py-2.5 text-center">
                      {r.double_count_risk ? (
                        <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-bold text-rose-700">Ya · {rupiah(r.overcount_risk)}</span>
                      ) : r.needs_cash_verification ? (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700">Cek tunai</span>
                      ) : (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700">Aman</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-slate-400">{result.note}</p>
        </div>
      )}
    </div>
  );
}

function AutoCancelDryRun() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = useCallback(async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get("/admin/audit/auto-cancel-candidates");
      setResult(data);
      toast.success(`Simulasi selesai — ${data.would_cancel_count} akan dibatalkan`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const decisionChip = (d) =>
    d === "Akan Dibatalkan"
      ? "bg-rose-100 text-rose-700"
      : "bg-emerald-100 text-emerald-700";

  return (
    <div data-testid="auto-cancel-dryrun">
      <div className="rounded-2xl border border-rose-100/70 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-500">
            Simulasi murni read-only. <b>Tidak</b> mengubah status, melepas slot, mengirim WA, atau membatalkan Midtrans.
          </p>
          <button
            data-testid="dryrun-run-btn"
            onClick={run}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl bg-[#6A38C7] px-4 py-2 text-sm font-bold text-white hover:bg-[#5a2eb0] disabled:opacity-50"
          >
            <Clock size={16} weight="bold" /> {loading ? "Menjalankan…" : "Jalankan Dry-run"}
          </button>
        </div>
      </div>

      {result && (
        <div className="mt-4">
          <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
            <span className={`rounded-full px-3 py-1 text-xs font-bold ${result.auto_cancel_enabled ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>
              Flag: {result.auto_cancel_enabled ? "AKTIF" : "NONAKTIF (aman)"}
            </span>
            <span className="text-slate-500">Total kandidat: <b>{result.count}</b></span>
            <span className="text-slate-500">Akan dibatalkan: <b className="text-rose-600">{result.would_cancel_count}</b></span>
            <span className="text-slate-500">Dikecualikan: <b className="text-emerald-600">{result.excluded_count}</b></span>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-rose-100/70 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-100 bg-slate-50 text-xs text-slate-500">
                <tr>
                  <th className="px-3 py-2.5 font-semibold">Kode</th>
                  <th className="px-3 py-2.5 font-semibold">Nama</th>
                  <th className="px-3 py-2.5 font-semibold">Dibuat</th>
                  <th className="px-3 py-2.5 font-semibold">Batas Bayar</th>
                  <th className="px-3 py-2.5 font-semibold">Status</th>
                  <th className="px-3 py-2.5 font-semibold text-center">Ledger</th>
                  <th className="px-3 py-2.5 font-semibold text-center">Bukti</th>
                  <th className="px-3 py-2.5 font-semibold">Midtrans</th>
                  <th className="px-3 py-2.5 font-semibold">Alasan</th>
                  <th className="px-3 py-2.5 font-semibold text-center">Keputusan</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.length === 0 && (
                  <tr><td colSpan={10} className="px-4 py-6 text-center text-slate-400">Tidak ada kandidat kedaluwarsa.</td></tr>
                )}
                {result.rows.map((r, i) => (
                  <tr key={i} data-testid={`dryrun-row-${r.kode_reservasi}`} className="border-b border-slate-50 last:border-0">
                    <td className="px-3 py-2.5 font-mono font-bold text-slate-700">{r.kode_reservasi}</td>
                    <td className="px-3 py-2.5 text-slate-600">{r.nama_masked}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{String(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{String(r.payment_expires_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{r.status} / {r.payment_status}</td>
                    <td className="px-3 py-2.5 text-center">{r.has_ledger ? "✓" : "–"}</td>
                    <td className="px-3 py-2.5 text-center">{r.has_manual_proof ? "✓" : "–"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{r.midtrans_status}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{r.reason}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${decisionChip(r.decision)}`}>{r.decision}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-slate-400">{result.note}</p>
        </div>
      )}
    </div>
  );
}

function ShadowOmzet() {
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(today.slice(0, 8) + "01");
  const [end, setEnd] = useState(today);
  const [orderNo, setOrderNo] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = useCallback(async (opts = {}) => {
    const s = opts.start ?? start;
    const e = opts.end ?? end;
    const ord = opts.order_no ?? "";
    setLoading(true);
    setResult(null);
    try {
      const q = ord
        ? `order_no=${encodeURIComponent(ord)}&start=${s}&end=${e}`
        : `start=${s}&end=${e}`;
      const { data } = await api.get(`/admin/audit/omzet-shadow?${q}`);
      setResult(data);
      if (ord && data.found === false) toast.error("Order tidak ditemukan");
      else toast.success("Perbandingan shadow selesai");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  const setRange = (s, e) => { setStart(s); setEnd(e); setOrderNo(""); run({ start: s, end: e }); };
  const yr = today.slice(0, 4);

  const downloadJson = () => {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `omzet-shadow-${orderNo || start + "_" + end}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const d = result?.detail;

  return (
    <div data-testid="shadow-omzet">
      <div className="rounded-2xl border border-rose-100/70 bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Dari</label>
            <input data-testid="shadow-start" type="date" value={start} onChange={(e) => setStart(e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-[#6A38C7] focus:outline-none" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Sampai</label>
            <input data-testid="shadow-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-[#6A38C7] focus:outline-none" />
          </div>
          <button data-testid="shadow-run-btn" onClick={() => { setOrderNo(""); run(); }} disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl bg-[#6A38C7] px-4 py-2 text-sm font-bold text-white hover:bg-[#5a2eb0] disabled:opacity-50">
            <MagnifyingGlass size={16} weight="bold" /> {loading ? "Menghitung…" : "Bandingkan (Shadow)"}
          </button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-slate-400">Periode cepat:</span>
          <button data-testid="shadow-range-aug" onClick={() => setRange("2026-08-01", "2026-08-31")}
            className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600 hover:bg-[#EEE6FF] hover:text-[#6A38C7]">Agustus 2026</button>
          <button data-testid="shadow-range-current" onClick={() => setRange(today.slice(0, 8) + "01", today)}
            className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600 hover:bg-[#EEE6FF] hover:text-[#6A38C7]">Bulan berjalan</button>
          <button data-testid="shadow-range-year" onClick={() => setRange(`${yr}-01-01`, `${yr}-12-31`)}
            className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600 hover:bg-[#EEE6FF] hover:text-[#6A38C7]">Seluruh {yr}</button>
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-slate-100 pt-3">
          <div className="flex-1 min-w-[200px]">
            <label className="mb-1 block text-xs font-semibold text-slate-500">Atau shadow per order (abaikan tanggal)</label>
            <input data-testid="shadow-order-input" value={orderNo} onChange={(e) => setOrderNo(e.target.value.trim())}
              placeholder="GBS-YYYYMM-000"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-[#6A38C7] focus:outline-none" />
          </div>
          <button data-testid="shadow-order-run" onClick={() => run({ order_no: orderNo })} disabled={loading || !orderNo}
            className="rounded-xl bg-slate-700 px-3 py-2 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50">Shadow Order</button>
          {["GBS-202608-014", "GBS-202608-011", "GBS-202608-013"].map((c) => (
            <button key={c} data-testid={`shadow-order-quick-${c}`} onClick={() => { setOrderNo(c); run({ order_no: c }); }}
              className="rounded-lg bg-slate-100 px-2.5 py-1 font-mono text-xs font-bold text-slate-600 hover:bg-[#EEE6FF] hover:text-[#6A38C7]">{c}</button>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-400">Read-only. Angka baru tidak menggantikan Omzet live sampai Anda menyetujui.</p>
      </div>

      {result && d && (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold text-slate-400">Omzet Lama (live)</p>
              <p data-testid="shadow-omzet-lama" className="mt-1 text-2xl font-extrabold text-slate-700">{rupiah(result.omzet_lama)}</p>
              <p className="text-[11px] text-slate-400">Metode sekarang (Σ harga reservasi anak)</p>
            </div>
            <div className="rounded-2xl border border-[#6A38C7]/30 bg-[#EEE6FF]/50 p-4">
              <p className="text-xs font-semibold text-[#6A38C7]">Nilai Tagihan Baru</p>
              <p data-testid="shadow-nilai-tagihan" className="mt-1 text-2xl font-extrabold text-[#6A38C7]">{rupiah(result.nilai_tagihan_baru)}</p>
              <p className="text-[11px] text-slate-500">Tunggal {d.single_count} · Multi {d.multi_count} (grand_total 1×)</p>
            </div>
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-xs font-semibold text-emerald-600">Pendapatan Diterima Baru</p>
              <p data-testid="shadow-pendapatan" className="mt-1 text-2xl font-extrabold text-emerald-700">{rupiah(result.pendapatan_diterima_baru)}</p>
              <p className="text-[11px] text-slate-500">Uang terverifikasi − refund</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div className="rounded-xl bg-white p-3 border border-rose-100/70">
              <p className="text-xs text-slate-400">Selisih Tagihan vs Lama</p>
              <p className={`font-bold ${result.selisih_tagihan_vs_lama < 0 ? "text-rose-600" : "text-emerald-600"}`}>{rupiah(result.selisih_tagihan_vs_lama)}</p>
            </div>
            <div className="rounded-xl bg-white p-3 border border-rose-100/70">
              <p className="text-xs text-slate-400">DP Diterima</p>
              <p className="font-bold text-slate-700">{rupiah(d.dp_received)}</p>
            </div>
            <div className="rounded-xl bg-white p-3 border border-rose-100/70">
              <p className="text-xs text-slate-400">Refund</p>
              <p className="font-bold text-slate-700">{rupiah(d.refund_total)}</p>
            </div>
            <div className="rounded-xl bg-white p-3 border border-amber-200">
              <p className="text-xs text-amber-600">Perlu Verifikasi Kas</p>
              <p className="font-bold text-amber-700">{d.needs_cash_count} · {rupiah(d.needs_cash_total)}</p>
            </div>
          </div>

          <div>
            <h3 className="mb-2 text-sm font-bold text-slate-600">Klasifikasi Pendapatan (menurut status ledger)</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-xs font-semibold text-emerald-600">Terverifikasi Sistem</p>
                <p data-testid="shadow-class-system" className="mt-1 text-xl font-extrabold text-emerald-700">{rupiah(d.klasifikasi?.verified_system)}</p>
                <p className="text-[11px] text-slate-500">Midtrans / manual berbukti</p>
              </div>
              <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4">
                <p className="text-xs font-semibold text-amber-600">Tunai/Manual — Perlu Rekonsiliasi Kas</p>
                <p data-testid="shadow-class-cash" className="mt-1 text-xl font-extrabold text-amber-700">{rupiah(d.klasifikasi?.cash_perlu_rekonsiliasi)}</p>
                <p className="text-[11px] text-slate-500">Terverifikasi di ledger, belum dicocokkan ke kas</p>
              </div>
              <div className="rounded-2xl border border-slate-300 bg-slate-50 p-4">
                <p className="text-xs font-semibold text-slate-500">Total Menurut Ledger</p>
                <p data-testid="shadow-class-total" className="mt-1 text-xl font-extrabold text-slate-700">{rupiah(d.klasifikasi?.total_menurut_ledger)}</p>
                <p className="text-[11px] text-slate-500">Sistem + kas − refund</p>
              </div>
            </div>
            <p className="mt-2 text-xs text-amber-600">⚠ Angka tunai/manual tetap terpisah sampai tim memastikan penerimaan kas.</p>
          </div>

          <div className="flex justify-end">
            <button data-testid="shadow-download-btn" onClick={downloadJson} className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200">
              <DownloadSimple size={14} weight="bold" /> Unduh JSON Termasking
            </button>
          </div>

          <div>
            <h3 className="mb-2 text-sm font-bold text-slate-600">20 Transaksi dengan Selisih Terbesar (Tagihan − Diterima)</h3>
            <div className="overflow-x-auto rounded-2xl border border-rose-100/70 bg-white">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-100 bg-slate-50 text-xs text-slate-500">
                  <tr>
                    <th className="px-3 py-2.5 font-semibold">Ref</th>
                    <th className="px-3 py-2.5 font-semibold">Jenis</th>
                    <th className="px-3 py-2.5 font-semibold">Nama</th>
                    <th className="px-3 py-2.5 font-semibold text-center">Item</th>
                    <th className="px-3 py-2.5 font-semibold text-right">Σ Harga Anak</th>
                    <th className="px-3 py-2.5 font-semibold text-right">Nilai Tagihan</th>
                    <th className="px-3 py-2.5 font-semibold text-right">Diterima</th>
                    <th className="px-3 py-2.5 font-semibold text-right">Selisih</th>
                    <th className="px-3 py-2.5 font-semibold">Penyebab</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.top_discrepancies || []).length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-6 text-center text-slate-400">Tidak ada selisih.</td></tr>
                  )}
                  {(d.top_discrepancies || []).map((row, i) => (
                    <tr key={i} data-testid={`shadow-disc-${row.ref}`} className="border-b border-slate-50 last:border-0 align-top">
                      <td className="px-3 py-2.5 font-mono font-bold text-slate-700">{row.ref}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">{row.scope === "multi" ? "Multi" : "Tunggal"}</td>
                      <td className="px-3 py-2.5 text-slate-600">{row.nama_masked}</td>
                      <td className="px-3 py-2.5 text-center text-slate-600">{row.scope === "multi" ? row.children_count : "-"}</td>
                      <td className="px-3 py-2.5 text-right text-slate-600">{row.scope === "multi" ? rupiah(row.child_harga_sum) : "-"}</td>
                      <td className="px-3 py-2.5 text-right font-bold text-[#6A38C7]">{rupiah(row.billing)}</td>
                      <td className="px-3 py-2.5 text-right font-bold text-emerald-600">{rupiah(row.received)}</td>
                      <td className={`px-3 py-2.5 text-right font-bold ${row.selisih !== 0 ? "text-rose-600" : "text-slate-400"}`}>{rupiah(row.selisih)}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">
                        <ul className="list-disc pl-4">
                          {(row.reasons || []).map((rs, j) => <li key={j}>{rs}</li>)}
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs text-slate-400">{result.note}</p>
        </div>
      )}
    </div>
  );
}

export default function AuditReconciliation() {
  const [tab, setTab] = useState("multi");

  return (
    <div className="mx-auto max-w-6xl" data-testid="audit-reconciliation-page">
      <div className="mb-5">
        <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-slate-800">
          <ShieldCheck size={26} weight="duotone" className="text-[#6A38C7]" /> Audit &amp; Rekonsiliasi
        </h1>
        <p className="text-sm text-slate-500">Alat read-only Super Admin. Tidak ada tombol perbaikan/rekonsiliasi di tahap ini.</p>
      </div>

      <div className="mb-4 flex gap-2 rounded-2xl bg-rose-50/60 p-1.5">
        <TabBtn testid="tab-multi-order" active={tab === "multi"} onClick={() => setTab("multi")}>Audit Multi-Order</TabBtn>
        <TabBtn testid="tab-auto-cancel" active={tab === "auto"} onClick={() => setTab("auto")}>Dry-run Auto-Cancel</TabBtn>
        <TabBtn testid="tab-shadow-omzet" active={tab === "shadow"} onClick={() => setTab("shadow")}>Shadow Omzet</TabBtn>
      </div>

      {tab === "multi" && <MultiOrderAudit />}
      {tab === "auto" && <AutoCancelDryRun />}
      {tab === "shadow" && <ShadowOmzet />}
    </div>
  );
}
